import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Hope = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.hope-qc:About Hope Paragraph 1">
          Hope Town, or &quot;Municipalité de Hope Town,&quot; is a quaint
          municipality in{' '}
          <LinkText href="/rental-listings/quebec">Quebec (QC)</LinkText>,
          Canada, located in the eastern part of the province near the New
          Brunswick border. Its history is intertwined with the settlement of
          English-speaking immigrants, particularly Scottish and Irish, in the
          19th century. The local economy centers on traditional industries such
          as fishing, agriculture, and forestry, characteristic of rural areas
          in QC. While primarily English-speaking, it coexists within the
          predominantly French-speaking province. Hope Town’s picturesque
          coastal landscapes along the Gulf of St. Lawrence attract tourists
          interested in outdoor activities like hiking and fishing. While not as
          prominent as larger{' '}
          <LinkText href={neighborhoodsLink}>
            cities and neighborhoods in QC
          </LinkText>
          , Hope Town offers a serene and scenic escape in the province.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$800 - $1,200',
          bed2: '$1,000 - $1,500',
          bed3: '$1,200 - $2,000'
        }}
        rentReportCity={t('cities:city_name_montreal')}
        rentReportLink="https://liv.rent/blog/rent-reports/montreal/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.hope-qc:Apartment Hunting in Hope Paragraph 1">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , it’s easier to find cheap rentals in Hope, Quebec – although{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon. To start your search, you can refer to our
            guides on{' '}
            <LinkText href={neighborhoodsLink}>
              neighbourhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-qc:Apartments for rent Hope Paragraph">
              In Hope, apartments are a common form of housing you’ll find. They
              are often found in areas with more population density, suitable
              for singles, couples, or small families. The rental prices can
              range significantly based on the unit’s condition and amenities.
              <LinkText href={links.apartment}>
                Apartments for rent in Hope, QC
              </LinkText>{' '}
              generally require much less maintenance than houses, making them
              ideal for renters who don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-qc:Houses for rent Hope Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Hope, QC
              </LinkText>{' '}
              are often found in residential neighborhoods further from town
              centers. These are suitable for those with a higher budget, need
              extra space, or who have multiple roommates. Both partial houses
              and entire houses for rent are common, as well as both new builds
              and older homes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent LOCATION Paragraph">
              Townhouses are another popular form of housing in {{ cityName }}{' '}
              that combine the best qualities of both apartments and whole
              houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-qc:Condos for rent Hope Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              less common in Hope, QC. They often come with higher rent but
              provide more amenities. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.hope-qc:Short term rentals Hope Paragraph">
              Hope, Quebec, offers various employment prospects in sectors like
              transportation, logistics, and services. However, the city itself
              doesn’t host extensive academic institutions. As a result,{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
              aren’t as prevalent here as in larger cities. Nonetheless, the
              tranquility and slower pace of life are attractive to individuals
              or families seeking a quieter, more stable living situation. The
              choice of{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                whether to opt for short-term or not
              </LinkText>{' '}
              in Hope is less about flexibility and more about personal
              lifestyle preferences and work or family commitments in the area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo:Living in LOCATION Paragraph">
          <Paragraph>
            People from around the world look to Canada as a safe haven, and{' '}
            {{ cityName }} is one of those cities that truly has something for
            everyone.
          </Paragraph>
          <Paragraph>
            If you’re not sure that {{ cityName }} is the right city for you,
            check out some of these resources comparing the different cities.
          </Paragraph>
        </Trans>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo:Popular Neighbourhoods LOCATION Paragraph">
          {{ cityName }} is made up of smaller neighbourhoods with their own
          distinct features. For more information, you can check out the blog to
          learn about the different{' '}
          <LinkText
            href={
              isChinese
                ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
                : 'https://liv.rent/blog/neighbourhoods/'
            }
          >
            neighbourhoods in {{ cityName }}
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo:Local Landmarks LOCATION Paragraph">
          Canada is known for its impressive natural beauty and diverse culture
          from coast-to-coast. You can find a full list of cultural landmarks
          and attractions in {{ cityName }} on{' '}
          <LinkText href="https://www.canada.ca/en/services/culture/cultural-attractions.html">
            Canada.ca
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Hope
